// Shared attachment pieces for Delivery Hub queries (client portal + admin)
import { useRef } from 'react';
import { Paperclip, X, FileText } from 'lucide-react';
import { showToast } from '@/components/ui/Toast';

export interface AttachmentItem {
  url: string;
  name: string;
  size?: number | null;
  type?: string | null;
}

export const MAX_QUERY_FILES = 5;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ACCEPT = '.png,.jpg,.jpeg,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt';

const formatSize = (bytes?: number | null) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

/** Links to a query's or comment's attachments */
export function AttachmentList({ attachments }: { attachments?: AttachmentItem[] }) {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {attachments.map((a) => (
        <a
          key={a.url}
          href={a.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-2 py-1 text-xs rounded-md border border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100 max-w-[220px]"
          title={a.name}
        >
          <FileText size={12} className="flex-shrink-0 text-gray-500" />
          <span className="truncate">{a.name}</span>
          {a.size ? <span className="text-gray-400 flex-shrink-0">{formatSize(a.size)}</span> : null}
        </a>
      ))}
    </div>
  );
}

/** "Attach files" button plus the selected files, uploaded by the parent on submit */
export function FilePicker({
  files,
  onChange,
  disabled,
}: {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const picked = Array.from(list);
    const tooBig = picked.filter((f) => f.size > MAX_FILE_BYTES);
    if (tooBig.length > 0) showToast(`${tooBig.map((f) => f.name).join(', ')}: max 10 MB per file`, 'error');
    const next = [...files, ...picked.filter((f) => f.size <= MAX_FILE_BYTES)];
    if (next.length > MAX_QUERY_FILES) showToast(`You can attach up to ${MAX_QUERY_FILES} files`, 'error');
    onChange(next.slice(0, MAX_QUERY_FILES));
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => addFiles(e.target.files)}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || files.length >= MAX_QUERY_FILES}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 disabled:opacity-50"
      >
        <Paperclip size={14} />
        Attach files
        <span className="text-gray-400 font-normal">(up to {MAX_QUERY_FILES}, 10 MB each)</span>
      </button>
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {files.map((f, i) => (
            <span
              key={`${f.name}-${i}`}
              className="inline-flex items-center gap-1.5 px-2 py-1 text-xs rounded-md bg-gray-100 text-gray-700 max-w-[220px]"
            >
              <span className="truncate">{f.name}</span>
              <span className="text-gray-400 flex-shrink-0">{formatSize(f.size)}</span>
              <button
                type="button"
                onClick={() => onChange(files.filter((_, j) => j !== i))}
                className="text-gray-400 hover:text-gray-700"
                aria-label={`Remove ${f.name}`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
